import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import { registerServiceWorkerSafely, activateServiceWorker } from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getMessaging, isSupported } from 'firebase/messaging';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCs4ynwRb8q-ftaWnGaBCdWZT1kunIMu90",
  authDomain: "actmobile-dda9d.firebaseapp.com",
  databaseURL: "https://actmobile-dda9d.firebaseio.com",
  projectId: "actmobile-dda9d",
  storageBucket: "actmobile-dda9d.firebasestorage.app",
  messagingSenderId: "728762119993",
  appId: "1:728762119993:web:cce4ae0bd14e1d21cb1ae8",
  measurementId: "G-JFP5KPRZH1"
};

// ===== Platform Detection Functions =====

// Check if we're running in standalone mode (PWA)
const isInStandaloneMode = () => 
  window.matchMedia('(display-mode: standalone)').matches || 
  window.navigator.standalone === true;

// Detect if running in iOS simulator
const isIOSSimulator = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for standard simulator strings
  if (ua.includes('iPhone Simulator') || ua.includes('iPad Simulator')) {
    return true;
  }
  
  // Additional indicators that might suggest simulator
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isLocalhostOrNonStandard = window.location.hostname === 'localhost' || 
    window.location.hostname.includes('local') ||
    window.location.port === '3000' || 
    window.location.port === '8080';
  
  // In simulator, these features often behave differently 
  const hasNonStandardBehavior = isIOS && isLocalhostOrNonStandard;
  
  return hasNonStandardBehavior;
};

// Detect iOS version
const getIOSVersion = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  
  if (!isIOS) return null;
  
  const match = ua.match(/OS (\d+)_?(\d+)?/);
  if (match) {
    return {
      major: parseInt(match[1], 10),
      minor: match[2] ? parseInt(match[2], 10) : 0
    };
  }
  
  return null;
};

// ===== Application Initialization =====

// Initialize Firebase
const app = initializeApp(firebaseConfig);
let messaging = null;

// Initialize Firebase Messaging with better error handling
const initializeFirebaseMessaging = async () => {
  try {
    // First check if Firebase Messaging is supported in this environment
    const isMessagingSupported = await isSupported();
    
    if (!isMessagingSupported) {
      console.log('Firebase Messaging is not supported in this environment');
      return false;
    }
    
    messaging = getMessaging(app);
    console.log('Firebase Messaging initialized successfully');
    return true;
  } catch (err) {
    console.error('Error initializing Firebase Messaging:', err);
    return false;
  }
};

// Register Firebase messaging service worker with better error handling
const registerFirebaseMessagingServiceWorker = async () => {
  // Don't even attempt registration in iOS simulator
  if (isIOSSimulator()) {
    console.log('iOS Simulator detected - skipping Firebase service worker registration as it will likely fail');
    window.localStorage.setItem('using_fallback_notifications', 'true');
    return null;
  }
  
  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers not supported in this browser');
    window.localStorage.setItem('using_fallback_notifications', 'true');
    return null;
  }
  
  try {
    // Check for existing registration
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    let existingRegistration = null;
    
    for (const reg of existingRegistrations) {
      if (reg.active && reg.active.scriptURL.includes('firebase-messaging-sw.js')) {
        console.log('Firebase messaging service worker already registered:', reg.scope);
        return reg;
      }
    }
    
    // Detect platform specifics
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = isInStandaloneMode();
    
    // Add timestamp to prevent caching issues on iOS
    const swUrl = `/firebase-messaging-sw.js?v=${Date.now()}`;
    
    // Register with improved error handling
    const registration = await navigator.serviceWorker.register(swUrl, {
      scope: '/',
      updateViaCache: isIOS ? 'none' : 'imports'
    });
    
    console.log('Firebase messaging service worker registered:', registration.scope);
    
    // For iOS in standalone mode, we need to ensure the SW is activated
    if (isIOS && isStandalone && registration.installing) {
      await new Promise((resolve, reject) => {
        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          console.log('Service worker activation timed out - continuing anyway');
          resolve();
        }, 5000);
        
        const stateChangeListener = (e) => {
          if (e.target && e.target.state === 'activated') {
            console.log('Firebase messaging SW activated for iOS standalone');
            clearTimeout(timeout);
            if (registration.installing) {
              registration.installing.removeEventListener('statechange', stateChangeListener);
            }
            resolve();
          }
        };
        
        if (registration.installing) {
          registration.installing.addEventListener('statechange', stateChangeListener);
        } else {
          clearTimeout(timeout);
          resolve();
        }
      });
    }
    
    return registration;
  } catch (err) {
    console.error('Error registering firebase messaging service worker:', err);
    
    // For iOS, we should try again after a short delay, but with more safeguards
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
      console.log('iOS detected, trying to register SW again after delay');
      
      try {
        // Wait a moment and try again with simplified options
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const secondAttemptReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { 
          scope: '/'
        });
        console.log('Second attempt success:', secondAttemptReg.scope);
        return secondAttemptReg;
      } catch (secondErr) {
        console.error('Second attempt failed:', secondErr);
        
        // Flag for fallback notifications
        window.localStorage.setItem('using_fallback_notifications', 'true');
        return null;
      }
    }
    
    // Flag for fallback notifications
    window.localStorage.setItem('using_fallback_notifications', 'true');
    return null;
  }
};

// Initialize the application
const startApplication = async () => {
  // Check for iOS simulator first
  if (isIOSSimulator()) {
    console.log('iOS Simulator detected - some features may not work correctly');
    window.localStorage.setItem('is_ios_simulator', 'true');
    window.localStorage.setItem('using_fallback_notifications', 'true');
  } else {
    // Initialize Firebase Messaging
    const messagingSupported = await initializeFirebaseMessaging();
    
    // Only register SW if messaging is supported
    if (messagingSupported) {
      await registerFirebaseMessagingServiceWorker();
    } else {
      window.localStorage.setItem('using_fallback_notifications', 'true');
    }
  }
  
  // Render the React application
  ReactDOM.render(
    <React.StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </React.StrictMode>,
    document.getElementById('root')
  );
};

// Start the application
startApplication();

// Modified service worker registration with improved update handling
registerServiceWorkerSafely('/service-worker.js', 
  { scope: '/' },
  {
    onUpdate: (registration) => {
      // Only prompt if there's actually a new version
      if (registration?.waiting) {
        // For iOS standalone mode, we need to be careful about reload prompts
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isStandalone = isInStandaloneMode();
        
        if (isIOS && isStandalone) {
          // For iOS standalone, just silently update without prompting
          activateServiceWorker(registration);
        } else {
          // For other platforms, show the prompt
          if (window.confirm('New version available! Would you like to refresh?')) {
            activateServiceWorker(registration);
          }
        }
      }
    },
    onSuccess: (registration) => {
      console.log('Service Worker registered successfully.');
      
      // For iOS standalone, we want to ensure the user gets notified about permissions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isStandalone = isInStandaloneMode();
      
      if (isIOS && isStandalone && Notification.permission === 'default') {
        // Prompt for notification permission on PWA launch for iOS
        setTimeout(() => {
          try {
            Notification.requestPermission()
              .then(permission => {
                console.log('iOS standalone notification permission:', permission);
              })
              .catch(err => {
                console.error('iOS permission error:', err);
              });
          } catch (err) {
            console.error('Error requesting notification permission:', err);
          }
        }, 3000); // Short delay to ensure app is fully loaded
      }
    },
    onError: (error) => {
      console.error('Service worker registration failed:', error);
    }
  }
);

reportWebVitals();